/**
 * ISC License
 * Copyright 2025 Javier Diaz Chamorro
 *
 * Permission to use, copy, modify, and/or distribute this software for any purpose with or without fee is hereby
 * granted, provided that the above copyright notice and this permission notice appear in all copies.
 *
 * THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH REGARD TO THIS SOFTWARE INCLUDING
 * ALL IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL,
 * DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS,
 * WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE
 * OR PERFORMANCE OF THIS SOFTWARE.
 */

// Example of usage:
// const buttonStyles = style({
//   base: {
//     display: 'flex',
//   },
//   variants: {
//     visual: {
//       solid: { backgroundColor: '#FC8181', color: 'white' },
//       outline: { borderWidth: 1, borderColor: '#FC8181' },
//     },
//     size: {
//       sm: { padding: 4, fontSize: 12 },
//       lg: { padding: 8, fontSize: 24 },
//     },
//   },
//   // Optional: Add compound variants for specific combinations
//   compoundVariants: [
//     {
//       variants: { visual: 'solid', size: ['lg'] },
//       style: { fontWeight: 'bold' },
//     },
//   ],
//   defaultVariants: {
//     visual: 'solid',
//     size: 'sm',
//   }
// });

// Example of using a styled component with a custom prop
// const button = styled(Button)<{ otherProp: string }>((props) => {
//   position: props.otherProp ? 'absolute' : 'relative';
// });

import React, { type ComponentType, type ReactNode } from 'react';
import { ImageStyle, TextStyle, ViewStyle, StyleProp, StyleSheet } from 'react-native';

// Define a type that removes token strings from style properties
type ResolvedStyle = ViewStyle & TextStyle & ImageStyle;

// StyleObject now extends ResolvedStyle and accepts type-safe token references
// Generic parameter Tokens allows autocomplete and validation of token paths
// Default to TokenConfig for backward compatibility
type StyleObject<Tokens extends TokenConfig = TokenConfig> = {
  [K in keyof ResolvedStyle]?:
    | ResolvedStyle[K]
    | (string extends ResolvedStyle[K]
        ? TokenPaths<Tokens>
        : number extends ResolvedStyle[K]
          ? TokenPaths<Tokens>
          : ResolvedStyle[K] extends string | number | undefined
            ? TokenPaths<Tokens>
            : never);
};

// Define the VariantOptions type to ensure type safety in variant definitions
type VariantOptions<V, Tokens extends TokenConfig = TokenConfig> = {
  [P in keyof V]: {
    [K in keyof V[P]]: StyleObject<Tokens>;
  };
};

// BooleanVariantKey type for 'true' and 'false' string literals
type BooleanVariantKey = 'true' | 'false';

type CompoundVariant<V, Tokens extends TokenConfig = TokenConfig> = {
  variants: Partial<{
    [P in keyof V]: keyof V[P] | boolean | Array<keyof V[P]>;
  }>;
  style: StyleObject<Tokens>;
};

// DefaultVariants type
type DefaultVariants<V> = Partial<{
  [P in keyof V]: keyof V[P] | boolean;
}>;

// VariantStyleConfig type
type VariantStyleConfig<
  V extends VariantOptions<V, Tokens>,
  Tokens extends TokenConfig = TokenConfig,
> = {
  base?: StyleObject<Tokens>;
  variants: V;
  compoundVariants?: CompoundVariant<V, Tokens>[];
  defaultVariants?: DefaultVariants<V>;
};

export type OmitUndefined<T> = T extends undefined ? never : T;

/**
 * Helper type to make all properties optional if they have a default value
 */
type OptionalIfHasDefault<Props, Defaults> = Omit<Props, keyof Defaults> &
  Partial<Pick<Props, Extract<keyof Defaults, keyof Props>>>;

export type VariantProps<T> = T extends (props?: infer P) => StyleProp<ResolvedStyle> ? P : never;

/**
 * Creates a function that generates styles based on variants
 */
function styles<V extends VariantOptions<V, Tokens>, Tokens extends TokenConfig = TokenConfig>(
  config: VariantStyleConfig<V, Tokens>,
) {
  type VariantProps = { [P in keyof V]: keyof V[P] | boolean | (string & {}) };
  type DefaultProps = NonNullable<typeof config.defaultVariants>;
  type Props = OptionalIfHasDefault<VariantProps, DefaultProps>;

  return (props?: Props): StyleProp<ResolvedStyle> => {
    // We'll build up styles in the correct hierarchy: base → variants → compound variants
    // Base styles (lowest priority)
    let stylesObj: StyleObject<Tokens> = {
      ...(config.base || {}),
    };

    // Merge default variants with provided props
    const mergedProps = {
      ...(config.defaultVariants || {}),
      ...props,
    } as VariantProps;

    // Apply variant styles (overrides base styles)
    for (const [propName, value] of Object.entries(mergedProps) as [
      keyof V,
      keyof VariantProps[keyof V] | boolean,
    ][]) {
      const variantGroup = config.variants[propName];
      if (variantGroup) {
        // Handle boolean variants
        if (typeof value === 'boolean') {
          const booleanValue: BooleanVariantKey = value ? 'true' : 'false';
          if (variantGroup[booleanValue as keyof typeof variantGroup]) {
            stylesObj = {
              ...stylesObj,
              ...variantGroup[booleanValue as keyof typeof variantGroup],
            };
          }
        } else {
          // Handle string/enum variants
          const variantValue = value || config.defaultVariants?.[propName];
          if (variantValue && variantGroup[variantValue as keyof typeof variantGroup]) {
            stylesObj = {
              ...stylesObj,
              ...variantGroup[variantValue as keyof typeof variantGroup],
            };
          }
        }
      }
    }

    // Apply compound variants (highest priority before inline styles)
    if (config.compoundVariants) {
      for (const compound of config.compoundVariants) {
        if (
          Object.entries(compound.variants).every(([propName, value]) => {
            // Handle boolean values in compound variants
            if (typeof value === 'boolean') {
              return mergedProps[propName as keyof V] === value;
            }

            // Handle array of values
            if (Array.isArray(value)) {
              return value.includes(mergedProps[propName as keyof V]);
            }

            // Handle single value (string/enum)
            return mergedProps[propName as keyof V] === value;
          })
        ) {
          stylesObj = {
            ...stylesObj,
            ...compound.style,
          };
        }
      }
    }

    // Create a StyleSheet for better performance
    return StyleSheet.create({
      style: stylesObj as ResolvedStyle,
    }).style;
  };
}

/**** Tokens definition */

// Define allowed token categories and their value types
interface AllowedTokenCategories {
  colors: string;
  space: number;
  fontSize: number;
  fonts: string;
  lineHeight: number;
}

// Update TokenConfig to only allow specific categories
type TokenConfig = {
  [K in keyof AllowedTokenCategories]?: Record<string, AllowedTokenCategories[K]>;
};

// Helper to convert keys (including numeric) to string literals
type KeysToStrings<T> =
  T extends Record<string | number, any> ? `${keyof T & (string | number)}` : never;

// Generate literal string types for all valid token paths from a config
// Example: For config { colors: { white: '#fff', primary: '#000' }, space: { 1: 4, 2: 8 } }
// Generates: "$colors.white" | "$colors.primary" | "$space.1" | "$space.2"
type TokenPaths<T extends TokenConfig> = {
  [Category in keyof T]: T[Category] extends Record<string | number, any>
    ? `$${Category & string}.${KeysToStrings<T[Category]>}`
    : never;
}[keyof T];

// Helper type to extract component props
type ComponentProps<T> = T extends ComponentType<infer P> ? P : never;

type StyledFunction<Tokens extends TokenConfig = TokenConfig> = <C extends ComponentType<any>>(
  Component: C,
) => <P extends object = {}>(
  styleObject:
    | StyleObject<Tokens>
    | ((props: P & Omit<ComponentProps<C>, 'style'>) => StyleObject<Tokens>),
) => ComponentType<
  P & Omit<ComponentProps<C>, 'style'> & { style?: StyleProp<ViewStyle | TextStyle | ImageStyle> }
>;

interface CreateTokensReturn<Tokens extends TokenConfig = TokenConfig> {
  sva: <V extends VariantOptions<V, Tokens>>(
    config: VariantStyleConfig<V, Tokens>,
  ) => (props?: any) => StyleProp<ResolvedStyle>;
  tokens: Readonly<Tokens>;
  styled: StyledFunction<Tokens>;
}

// Helper to resolve token references in style objects
function resolveTokens<Tokens extends TokenConfig>(
  style: StyleObject<Tokens>,
  tokens: Tokens,
): StyleObject<Tokens> {
  return Object.entries(style).reduce<Record<string, ResolvedStyle[keyof ResolvedStyle]>>(
    (acc, [key, value]) => {
      if (typeof value !== 'string' || !value.startsWith('$')) {
        acc[key] = value;
        return acc;
      }

      const tokenPath = value.slice(1).split('.');
      const [category, token] = tokenPath;

      const tokenCategory = tokens[category as keyof Tokens] as
        | Record<string, string | number>
        | undefined;
      const tokenValue = tokenCategory?.[token];
      if (tokenValue !== undefined) {
        acc[key] = tokenValue;
      }

      return acc;
    },
    {},
  ) as StyleObject<Tokens>;
}

/**
 * Creates a token system and returns the styles function
 */
export function defineTokens<T extends TokenConfig>(tokenConfig: T): CreateTokensReturn<T> {
  // Create the tokens object
  const tokens = Object.entries(tokenConfig).reduce((acc, [category, values]) => {
    return {
      ...acc,
      [category]: values,
    };
  }, {} as T);

  // Create a wrapped styles function that resolves token references
  const sva = <V extends VariantOptions<V, T>>(config: VariantStyleConfig<V, T>) => {
    // Resolve tokens in base styles
    const resolvedBase = config.base ? resolveTokens(config.base, tokens) : config.base;

    // Resolve tokens in variants
    const resolvedVariants = config.variants
      ? (Object.entries(config.variants).reduce<Partial<V>>((acc, [key, variantGroup]) => {
          type VariantGroupType = Record<string, StyleObject<T>>;

          const resolvedGroup = Object.entries(variantGroup as VariantGroupType).reduce<
            Record<string, StyleObject<T>>
          >((groupAcc, [variantKey, variantStyles]) => {
            return {
              ...groupAcc,
              [variantKey]: resolveTokens(variantStyles, tokens),
            };
          }, {});

          return {
            ...acc,
            [key as keyof V]: resolvedGroup as V[keyof V],
          };
        }, {}) as V)
      : ({} as V);

    // Resolve tokens in compound variants
    const resolvedCompoundVariants = config.compoundVariants?.map((compound) => ({
      ...compound,
      style: resolveTokens(compound.style, tokens),
    }));

    // Return the styles function with resolved tokens
    return styles({
      ...config,
      base: resolvedBase,
      variants: resolvedVariants,
      compoundVariants: resolvedCompoundVariants,
    });
  };

  /**
   * Styled function for creating styled components with token-aware styles
   */
  const styled: StyledFunction<T> = <C extends ComponentType<any>>(Component: C) => {
    return <P extends object = {}>(
      styleObject:
        | StyleObject<T>
        | ((props: P & Omit<ComponentProps<C>, 'style'>) => StyleObject<T>),
    ) => {
      // Create and return a new component that applies the resolved styles
      const StyledComponent: ComponentType<
        P &
          Omit<ComponentProps<C>, 'style'> & {
            style?: StyleProp<ViewStyle | TextStyle | ImageStyle>;
          }
      > = (props) => {
        const {
          children,
          style: propStyle,
          ...rest
        } = props as {
          children?: ReactNode;
          style?: StyleProp<ViewStyle | TextStyle | ImageStyle>;
        };

        // If styleObject is a function, call it with props to get the style object
        const styleToResolve =
          typeof styleObject === 'function'
            ? styleObject(props as P & Omit<ComponentProps<C>, 'style'>)
            : styleObject;

        // Resolve tokens in the style object
        const resolvedStyle = resolveTokens(styleToResolve, tokens);

        // Create StyleSheet for the resolved style
        const styles = StyleSheet.create({
          style: resolvedStyle as ResolvedStyle,
        });

        // Styles hierarchy: base styles → variants → compound variants → inline styles
        // Inline styles (propStyle) must have highest priority, so they come last in the array
        const mergedStyle = propStyle
          ? [styles.style, ...(Array.isArray(propStyle) ? propStyle : [propStyle])]
          : styles.style;

        // We need to cast here to handle the component props correctly
        const componentProps = {
          ...rest,
          style: mergedStyle,
        } as ComponentProps<C>;

        // Use createElement instead of JSX to avoid syntax issues
        return React.createElement(Component, componentProps, children);
      };

      // Set display name for better debugging
      const componentName = Component.displayName || Component.name || 'Component';
      StyledComponent.displayName = `Styled(${componentName})`;

      return StyledComponent;
    };
  };

  return {
    sva,
    tokens,
    styled,
  };
}
